<template>
  <SkeletonLoader>
    <div class="wiki-skeleton">
      <!-- Header: title + 2 action buttons -->
      <div class="wiki-skeleton__header">
        <div class="sk-line sk-line--title" style="width: 25%;"></div>
        <div class="wiki-skeleton__header-actions">
          <div class="sk-rect" style="width: 130px; height: 38px; border-radius: 6px;"></div>
          <div class="sk-rect" style="width: 150px; height: 38px; border-radius: 6px;"></div>
        </div>
      </div>

      <!-- Stats row: 4 stat cards -->
      <div class="wiki-skeleton__stats">
        <div v-for="i in 4" :key="'stat-' + i" class="wiki-skeleton__stat-card">
          <div class="sk-circle sk-circle--md"></div>
          <div class="wiki-skeleton__stat-text">
            <div class="sk-line sk-line--lg" :style="{ width: statNumberWidths[i] }"></div>
            <div class="sk-line sk-line--sm" :style="{ width: statLabelWidths[i] }"></div>
          </div>
        </div>
      </div>

      <!-- Search/filter bar: search input + tag filter pills -->
      <div class="wiki-skeleton__filters">
        <div class="sk-rect" style="width: 280px; height: 38px; border-radius: 6px;"></div>
        <div class="wiki-skeleton__tags">
          <div
            v-for="i in 5"
            :key="'tag-' + i"
            class="sk-rect"
            :style="{ width: tagWidths[i], height: '30px', borderRadius: '15px' }"
          ></div>
        </div>
      </div>

      <!-- Wiki table: header row -->
      <div class="wiki-skeleton__table-header">
        <div class="sk-col" style="flex: 3;">
          <div class="sk-line sk-line--sm" style="width: 40%;"></div>
        </div>
        <div class="sk-col" style="flex: 2;">
          <div class="sk-line sk-line--sm" style="width: 30%;"></div>
        </div>
        <div class="sk-col wiki-skeleton__date-col" style="flex: 1;">
          <div class="sk-line sk-line--sm" style="width: 50%;"></div>
        </div>
      </div>

      <!-- Wiki table: 6 page rows -->
      <div
        v-for="i in 6"
        :key="'row-' + i"
        class="wiki-skeleton__row"
      >
        <div class="sk-col" style="flex: 3;">
          <div class="sk-line" :style="{ width: rowTitleWidths[i] }"></div>
        </div>
        <div class="sk-col" style="flex: 2;">
          <div class="wiki-skeleton__row-tags">
            <div
              v-for="j in rowTagCounts[i]"
              :key="'row-' + i + '-tag-' + j"
              class="sk-rect"
              :style="{ width: rowTagWidths[i][j], height: '22px', borderRadius: '11px' }"
            ></div>
          </div>
        </div>
        <div class="sk-col wiki-skeleton__date-col" style="flex: 1;">
          <div class="sk-line sk-line--sm" :style="{ width: rowDateWidths[i] }"></div>
        </div>
      </div>
    </div>
  </SkeletonLoader>
</template>

<script>
import SkeletonLoader from '@/components/SkeletonLoader.vue';

export default {
  name: 'WikiHomeSkeleton',
  components: { SkeletonLoader },
  data() {
    return {
      statNumberWidths: { 1: '50%', 2: '45%', 3: '55%', 4: '40%' },
      statLabelWidths: { 1: '70%', 2: '65%', 3: '75%', 4: '60%' },
      tagWidths: { 1: '72px', 2: '88px', 3: '64px', 4: '96px', 5: '80px' },
      rowTitleWidths: { 1: '70%', 2: '55%', 3: '80%', 4: '60%', 5: '75%', 6: '50%' },
      rowTagCounts: { 1: 2, 2: 3, 3: 1, 4: 2, 5: 3, 6: 2 },
      rowTagWidths: {
        1: { 1: '52px', 2: '68px' },
        2: { 1: '60px', 2: '48px', 3: '72px' },
        3: { 1: '64px' },
        4: { 1: '56px', 2: '76px' },
        5: { 1: '44px', 2: '64px', 3: '52px' },
        6: { 1: '72px', 2: '56px' },
      },
      rowDateWidths: { 1: '70%', 2: '60%', 3: '75%', 4: '65%', 5: '55%', 6: '70%' },
    };
  }
}
</script>

<style lang="scss" scoped>
.wiki-skeleton {
  padding: 20px;
}

.wiki-skeleton__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.wiki-skeleton__header-actions {
  display: flex;
  gap: 10px;
}

.wiki-skeleton__stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.wiki-skeleton__stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.wiki-skeleton__stat-text {
  flex: 1;
  min-width: 0;
}

.wiki-skeleton__filters {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.wiki-skeleton__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.wiki-skeleton__table-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 2px solid #e2e8f0;
}

.wiki-skeleton__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  border-bottom: 1px solid #f1f5f9;
}

.wiki-skeleton__row-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.wiki-skeleton__date-col {
  min-width: 80px;
}

/* Tablet: stat cards 2x2 */
@media (max-width: 1024px) {
  .wiki-skeleton__stats {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Mobile */
@media (max-width: 768px) {
  .wiki-skeleton {
    padding: 12px;
  }

  .wiki-skeleton__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .wiki-skeleton__stats {
    grid-template-columns: 1fr;
  }

  .wiki-skeleton__filters {
    flex-direction: column;
    align-items: stretch;

    .sk-rect:first-child {
      width: 100% !important;
    }
  }

  .wiki-skeleton__tags {
    flex-wrap: wrap;
  }

  .wiki-skeleton__date-col {
    display: none;
  }
}
</style>
